/**
 * useAIProviders.ts — US-243: React Query hooks for provider credentials and policy (Epic 34)
 *
 * Hooks:
 *   useProviderCredentials()    — GET /api/provider-credentials
 *   useSaveCredential()         — POST /api/provider-credentials
 *   useValidateCredential()     — POST /api/provider-credentials/validate
 *   useRevokeCredential()       — POST /api/provider-credentials/:id/revoke
 *   useRotateCredential()       — POST /api/provider-credentials/:id/rotate
 *   useProviderPolicy()         — GET /api/provider-policies
 *   useUpdateProviderPolicy()   — PUT /api/provider-policies/:provider
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------
export const providerQueryKeys = {
  credentials: ['providerCredentials'] as const,
  policies: ['providerPolicies'] as const,
};

// ---------------------------------------------------------------------------
// Credentials
// ---------------------------------------------------------------------------

/**
 * Fetch all provider credentials for the current tenant.
 * Returns safe metadata only (no plaintext secrets).
 */
export function useProviderCredentials() {
  return useQuery({
    queryKey: providerQueryKeys.credentials,
    queryFn: () => api.getProviderCredentials(),
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Save a new provider credential (POST /api/provider-credentials).
 * On success, invalidates the credentials query.
 */
export function useSaveCredential() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      provider: string;
      label: string;
      secret: string;
      scopes?: string[];
    }) => api.saveProviderCredential(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: providerQueryKeys.credentials });
    },
  });
}

/**
 * Validate a provider key without persisting it.
 */
export function useValidateCredential() {
  return useMutation({
    mutationFn: (data: { provider: string; secret: string }) =>
      api.validateProviderKey(data.provider, data.secret),
  });
}

/**
 * Rotate a credential (generates new key entry, revokes old one).
 */
export function useRotateCredential() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.rotateProviderCredential(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: providerQueryKeys.credentials });
    },
  });
}

/**
 * Revoke a credential (soft-delete, no recovery).
 */
export function useRevokeCredential() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.revokeProviderCredential(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: providerQueryKeys.credentials });
    },
  });
}

/**
 * Enable a previously-disabled credential.
 */
export function useEnableCredential() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.enableProviderCredential(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: providerQueryKeys.credentials });
    },
  });
}

/**
 * Disable (but not revoke) a credential.
 */
export function useDisableCredential() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.disableProviderCredential(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: providerQueryKeys.credentials });
    },
  });
}

// ---------------------------------------------------------------------------
// Provider Policy
// ---------------------------------------------------------------------------

/**
 * Fetch the provider enablement policies.
 */
export function useProviderPolicy() {
  return useQuery({
    queryKey: providerQueryKeys.policies,
    queryFn: () => api.getProviderPolicy(),
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Update the enabled/disabled policy for a provider.
 */
export function useUpdateProviderPolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { provider: string; enabled: boolean }) =>
      api.updateProviderPolicy(data.provider, { enabled: data.enabled }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: providerQueryKeys.policies });
    },
  });
}
